/**
 * Scheduling control for a service whose interval crosses partway through an
 * event: run it BEFORE (in the shop) or DURING (between rounds).
 *
 * The server decides that the choice *exists* — `TimelineOccurrence.choice` is
 * true only when a crossing lands strictly inside an event's estimated passes
 * (services/timeline.ts). Which option the user picks is browser-local and
 * unpersisted; see lib/schedule.tsx for why, and what it would take to fix.
 */

import { MapPin, Wrench, Zap } from 'lucide-react';

import type { TimelineOccurrence } from '@svcdue/contracts';

import type { Plan } from '../lib/schedule';

export function MidEventControl({
  occurrence,
  eventLabel,
  plan,
  onPick,
}: {
  occurrence: TimelineOccurrence;
  /** The track this lands at — the timeline knows it, the occurrence does not. */
  eventLabel: string;
  plan: Plan;
  onPick: (plan: Plan) => void;
}) {
  // Guaranteed non-null when `choice` is true, but the contract types them
  // nullable because a time-driven crossing has no passes at all.
  const atPass = occurrence.atPass ?? 0;
  const ofPasses = occurrence.ofPasses ?? 0;

  return (
    <div className="me">
      <div className="me-flag">
        <Zap size={11} /> Crosses mid-event —{' '}
        <b>
          pass {atPass} of {ofPasses}
        </b>{' '}
        at {eventLabel}
      </div>
      {/* Where in the event it lands, drawn as the passes themselves. */}
      <div className="me-pass">
        {Array.from({ length: ofPasses }).map((_, i) => (
          <span key={i} className={'pp' + (i + 1 === atPass ? ' pp-cross' : i + 1 < atPass ? ' pp-done' : '')} />
        ))}
      </div>
      <div className="me-seg">
        <button className={'seg' + (plan === 'before' ? ' seg-on' : '')} onClick={() => onPick('before')}>
          Before
        </button>
        <button className={'seg' + (plan === 'during' ? ' seg-on' : '')} onClick={() => onPick('during')}>
          During
        </button>
      </div>
      <div className="me-note">
        {plan === 'before' ? (
          <>
            <Wrench size={11} /> Service in the shop before you tow — start round-ready.
          </>
        ) : (
          <>
            <MapPin size={11} /> Between rounds at ~pass {atPass} of {ofPasses}
          </>
        )}
      </div>
    </div>
  );
}
